/**
 * Stock Badge Live Updater
 * Handles real-time stock updates and variant changes
 */

class StockBadgeLiveUpdater {
  constructor() {
    this.badges = new Map();
    this.updateInterval = 30000; // 30 seconds
    this.init();
  }

  init() {
    // Initialize all stock badges on page load
    this.initializeBadges();
    
    // Listen for variant changes
    this.setupVariantListeners();
    
    // Start periodic updates
    this.startPeriodicUpdates();
    
    console.log('✅ Stock Badge Live Updater initialized');
  }

  initializeBadges() {
    const stockBadges = document.querySelectorAll('[data-product-id][data-variant-id]');
    
    stockBadges.forEach(badge => {
      const productId = badge.dataset.productId;
      const variantId = badge.dataset.variantId;
      
      if (productId && variantId) {
        this.badges.set(badge, {
          productId,
          variantId,
          element: badge,
          lastUpdate: Date.now()
        });
      }
    });
    
    console.log(`📊 Initialized ${this.badges.size} stock badges`);
  }

  setupVariantListeners() {
    // Listen for Shopify variant changes
    document.addEventListener('variant:change', (e) => {
      if (e.detail && e.detail.variant) {
        this.handleVariantChange(e.detail.variant);
      }
    });

    // Listen for custom variant events
    document.addEventListener('product:variant:change', (e) => {
      if (e.detail && e.detail.variant) {
        this.handleVariantChange(e.detail.variant);
      }
    });

    // Listen for form changes (fallback)
    const variantSelectors = document.querySelectorAll('input[name="id"], select[name="id"]');
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', (e) => {
        const variantId = e.target.value;
        if (variantId) {
          this.fetchVariantData(variantId);
        }
      });
    });
  }

  async handleVariantChange(variant) {
    console.log('🔄 Variant changed:', variant.id);
    
    this.badges.forEach((badgeData, badgeElement) => {
      if (badgeData.productId == variant.product_id || 
          badgeData.productId == variant.product?.id) {
        this.updateBadgeForVariant(badgeElement, variant);
        badgeData.variantId = variant.id;
        badgeData.lastUpdate = Date.now();
      }
    });
  }

  async fetchVariantData(variantId) {
    try {
      // Try to get variant data from the current product
      const productScripts = document.querySelectorAll('script[data-product-json]');
      
      for (const script of productScripts) {
        try {
          const productData = JSON.parse(script.textContent);
          const variant = productData.variants.find(v => v.id == variantId);
          
          if (variant) {
            this.handleVariantChange(variant);
            return;
          }
        } catch (e) {
          console.warn('Could not parse product data from script');
        }
      }
      
      // Fallback: fetch from API
      const response = await fetch(`/variants/${variantId}.js`);
      if (response.ok) {
        const variant = await response.json();
        this.handleVariantChange(variant);
      }
    } catch (error) {
      console.warn('Could not fetch variant data:', error);
    }
  }

  updateBadgeForVariant(badgeElement, variant) {
    const badge = badgeElement.querySelector('.stock-badge');
    const textElement = badgeElement.querySelector('.stock-badge__text');
    const iconElement = badgeElement.querySelector('.stock-badge__icon');
    
    if (!badge || !textElement) return;

    const quantity = variant.inventory_quantity || 0;
    const available = variant.available;
    const policy = variant.inventory_policy;
    const management = variant.inventory_management;

    // Get thresholds from data attributes or use defaults
    const lowThreshold = parseInt(badgeElement.dataset.lowThreshold) || 10;
    const criticalThreshold = parseInt(badgeElement.dataset.criticalThreshold) || 3;
    const showQuantity = badgeElement.dataset.showQuantity !== 'false';

    let status, text, icon;

    // Determine stock status
    if (!available) {
      status = 'out_of_stock';
      text = 'Sold Out';
      icon = '✗';
    } else if (management === 'shopify') {
      if (quantity > lowThreshold) {
        status = 'in_stock';
        text = 'In Stock';
        icon = '✓';
      } else if (quantity > criticalThreshold) {
        status = 'low_stock';
        text = showQuantity ? `Low Stock (${quantity} left)` : 'Low Stock';
        icon = '⚠';
      } else if (quantity > 0) {
        status = 'critical_stock';
        text = showQuantity ? `Only ${quantity} left!` : 'Last few items!';
        icon = '🔥';
      } else if (policy === 'continue') {
        status = 'pre_order';
        text = 'Pre-Order';
        icon = '→';
      } else {
        status = 'out_of_stock';
        text = 'Sold Out';
        icon = '✗';
      }
    } else {
      status = 'in_stock';
      text = 'In Stock';
      icon = '✓';
    }

    // Update badge content
    textElement.textContent = text;
    if (iconElement) iconElement.textContent = icon;

    // Update badge classes
    badge.className = badge.className.replace(/stock-badge--\w+/g, '');
    badge.classList.add(`stock-badge--${status}`);
    
    // Add style class back
    const styleClass = badgeElement.dataset.badgeStyle;
    if (styleClass) {
      badge.classList.add(`stock-badge--${styleClass}`);
    }

    // Update data attributes
    badgeElement.dataset.stockStatus = status;
    badgeElement.dataset.variantId = variant.id;

    // Trigger animation update
    this.triggerBadgeAnimation(badge);

    console.log(`📊 Updated badge for variant ${variant.id}: ${status} (${quantity} items)`);
  }

  triggerBadgeAnimation(badge) {
    // Reset animation to trigger it again
    badge.style.animation = 'none';
    badge.offsetHeight; // Force reflow
    badge.style.animation = null;
    
    // Add temporary highlight effect
    badge.style.transform = 'scale(1.05)';
    setTimeout(() => {
      badge.style.transform = '';
    }, 200);
  }

  startPeriodicUpdates() {
    setInterval(() => {
      this.updateAllBadges();
    }, this.updateInterval);
  }

  async updateAllBadges() {
    console.log('🔄 Performing periodic stock update...');
    
    const promises = Array.from(this.badges.entries()).map(([badgeElement, badgeData]) => {
      return this.fetchAndUpdateBadge(badgeElement, badgeData);
    });

    try {
      await Promise.all(promises);
      console.log('✅ Periodic update completed');
    } catch (error) {
      console.warn('⚠️ Some badges failed to update:', error);
    }
  }

  async fetchAndUpdateBadge(badgeElement, badgeData) {
    try {
      const response = await fetch(`/products/${badgeData.productId}.js`);
      if (!response.ok) return;
      
      const product = await response.json();
      const variant = product.variants.find(v => v.id == badgeData.variantId);
      
      if (variant) {
        this.updateBadgeForVariant(badgeElement, variant);
        badgeData.lastUpdate = Date.now();
      }
    } catch (error) {
      console.warn(`Failed to update badge for product ${badgeData.productId}:`, error);
    }
  }

  // Public method to manually update a specific badge
  updateBadge(badgeElement) {
    const badgeData = this.badges.get(badgeElement);
    if (badgeData) {
      this.fetchAndUpdateBadge(badgeElement, badgeData);
    }
  }

  // Public method to add new badges dynamically
  addBadge(badgeElement) {
    const productId = badgeElement.dataset.productId;
    const variantId = badgeElement.dataset.variantId;
    
    if (productId && variantId) {
      this.badges.set(badgeElement, {
        productId,
        variantId,
        element: badgeElement,
        lastUpdate: Date.now()
      });
      
      console.log(`📊 Added new stock badge for product ${productId}`);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.stockBadgeUpdater = new StockBadgeLiveUpdater();
});

// Initialize immediately if DOM is already loaded
if (document.readyState === 'loading') {
  // DOM is still loading
} else {
  // DOM is already loaded
  window.stockBadgeUpdater = new StockBadgeLiveUpdater();
}